import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    company_name: "",
    email: "",
    phone: "",
    gst_number: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email || !form.phone || !form.message) {
      setError("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      setForm({ name: "", company_name: "", email: "", phone: "", gst_number: "", message: "" });
    } catch {
      setError("Something went wrong. Please try again or contact us via WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" style={{ marginTop: "calc(36px + 96px)" }}>
      {/* Hero */}
      <section className="py-16 md:py-20 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="font-mono text-xs tracking-[0.3em] text-neutral-500 uppercase block mb-3">
            Contact Us
          </span>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl uppercase tracking-tight">
            Let's Talk Business
          </h1>
          <p className="text-neutral-400 mt-4 max-w-2xl text-base md:text-lg">
            Whether you need a quote, want to place a bulk order, or explore a retail partnership —
            reach out and our team will respond within 24 hours.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="font-heading font-extrabold text-2xl uppercase tracking-tight text-black mb-6">
                  Contact Information
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-neutral-100 flex items-center justify-center shrink-0">
                      <Phone className="w-4 h-4 text-[#D32F2F]" />
                    </div>
                    <div>
                      <span className="font-mono text-[10px] tracking-[0.2em] text-neutral-400 uppercase block mb-1">Phone</span>
                      <a href="tel:+918824893533" className="block text-sm text-black hover:text-[#D32F2F] transition-colors">+91 88248 93533</a>
                      <a href="tel:+919664206774" className="block text-sm text-black hover:text-[#D32F2F] transition-colors">+91 96642 06774</a>
                      <a href="tel:+919414456359" className="block text-sm text-black hover:text-[#D32F2F] transition-colors">+91 94144 56359</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-neutral-100 flex items-center justify-center shrink-0">
                      <Mail className="w-4 h-4 text-[#D32F2F]" />
                    </div>
                    <div>
                      <span className="font-mono text-[10px] tracking-[0.2em] text-neutral-400 uppercase block mb-1">Email</span>
                      <a href="mailto:rktraders.jp@gmail.com" className="text-sm text-black hover:text-[#D32F2F] transition-colors">
                        rktraders.jp@gmail.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-neutral-100 flex items-center justify-center shrink-0">
                      <MapPin className="w-4 h-4 text-[#D32F2F]" />
                    </div>
                    <div>
                      <span className="font-mono text-[10px] tracking-[0.2em] text-neutral-400 uppercase block mb-1">Address</span>
                      <p className="text-sm text-black">
                        1200/2, Behind Axis Bank,<br />
                        Barkat Nagar, Tonk Road,<br />
                        Jaipur 302015, Rajasthan
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-neutral-100 flex items-center justify-center shrink-0">
                      <Clock className="w-4 h-4 text-[#D32F2F]" />
                    </div>
                    <div>
                      <span className="font-mono text-[10px] tracking-[0.2em] text-neutral-400 uppercase block mb-1">Business Hours</span>
                      <p className="text-sm text-black">
                        Mon - Sat: 9:30 AM - 7:30 PM<br />
                        Sunday: Closed
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* GST Section */}
              <div
                data-testid="gst-section"
                className="border border-neutral-200 p-6"
              >
                <span className="font-mono text-[10px] tracking-[0.2em] text-neutral-400 uppercase block mb-2">
                  GST Identification Number
                </span>
                <span className="font-mono text-lg font-medium text-black">
                  08ABMPJ3350M1ZC
                </span>
                <p className="text-xs text-neutral-400 mt-2">
                  Registered under the Goods and Services Tax Act, Government of India.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <div className="border border-neutral-200 p-8 md:p-10">
                <h2 className="font-heading font-extrabold text-2xl uppercase tracking-tight text-black mb-2">
                  Send Us an Enquiry
                </h2>
                <p className="text-sm text-neutral-400 mb-8">
                  Fields marked with * are required.
                </p>

                {submitted ? (
                  <div data-testid="contact-success" className="bg-green-50 border border-green-200 p-8 text-center">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Send className="w-5 h-5 text-green-700" />
                    </div>
                    <h3 className="font-heading font-bold text-xl uppercase text-green-800 mb-2">
                      Enquiry Submitted
                    </h3>
                    <p className="text-green-700 text-sm">
                      Thank you for reaching out. Our team will get back to you within 24 hours.
                    </p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="mt-4 text-sm font-heading uppercase tracking-wider font-semibold text-green-700 hover:text-green-900"
                      data-testid="send-another-btn"
                    >
                      Send Another Enquiry
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name" className="font-heading text-xs uppercase tracking-wider font-semibold text-neutral-500 mb-2 block">
                          Full Name *
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          value={form.name}
                          onChange={handleChange}
                          placeholder="Your full name"
                          data-testid="input-name"
                          className="rounded-none border-neutral-300 h-12 bg-neutral-50 focus:ring-black focus:border-black"
                        />
                      </div>
                      <div>
                        <Label htmlFor="company_name" className="font-heading text-xs uppercase tracking-wider font-semibold text-neutral-500 mb-2 block">
                          Company Name
                        </Label>
                        <Input
                          id="company_name"
                          name="company_name"
                          value={form.company_name}
                          onChange={handleChange}
                          placeholder="Your company or shop name"
                          data-testid="input-company"
                          className="rounded-none border-neutral-300 h-12 bg-neutral-50 focus:ring-black focus:border-black"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="email" className="font-heading text-xs uppercase tracking-wider font-semibold text-neutral-500 mb-2 block">
                          Email Address *
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={form.email}
                          onChange={handleChange}
                          placeholder="your@email.com"
                          data-testid="input-email"
                          className="rounded-none border-neutral-300 h-12 bg-neutral-50 focus:ring-black focus:border-black"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone" className="font-heading text-xs uppercase tracking-wider font-semibold text-neutral-500 mb-2 block">
                          Phone Number *
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          value={form.phone}
                          onChange={handleChange}
                          placeholder="+91 XXXXX XXXXX"
                          data-testid="input-phone"
                          className="rounded-none border-neutral-300 h-12 bg-neutral-50 focus:ring-black focus:border-black"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="gst_number" className="font-heading text-xs uppercase tracking-wider font-semibold text-neutral-500 mb-2 block">
                        GST Number (Optional)
                      </Label>
                      <Input
                        id="gst_number"
                        name="gst_number"
                        value={form.gst_number}
                        onChange={handleChange}
                        placeholder="Your GST identification number"
                        data-testid="input-gst"
                        className="rounded-none border-neutral-300 h-12 bg-neutral-50 focus:ring-black focus:border-black"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message" className="font-heading text-xs uppercase tracking-wider font-semibold text-neutral-500 mb-2 block">
                        Your Message *
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={form.message}
                        onChange={handleChange}
                        placeholder="Tell us about your requirements — brands, parts, quantities..."
                        rows={5}
                        data-testid="input-message"
                        className="rounded-none border-neutral-300 bg-neutral-50 focus:ring-black focus:border-black resize-none"
                      />
                    </div>

                    {error && (
                      <p data-testid="contact-error" className="text-[#D32F2F] text-sm">
                        {error}
                      </p>
                    )}

                    <Button
                      type="submit"
                      disabled={submitting}
                      data-testid="contact-submit-btn"
                      className="bg-[#D32F2F] text-white hover:bg-[#B71C1C] h-12 px-10 text-sm font-heading uppercase font-bold tracking-wide rounded-none cta-btn w-full md:w-auto"
                    >
                      {submitting ? "Submitting..." : "Submit Enquiry"}
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Maps */}
      <section data-testid="map-section" className="bg-neutral-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-8">
            <span className="font-mono text-xs tracking-[0.3em] text-neutral-400 uppercase block mb-3">
              Visit Us
            </span>
            <h2 className="font-heading font-extrabold text-2xl sm:text-3xl uppercase tracking-tight text-black">
              Our Location
            </h2>
          </div>
          <div className="border border-neutral-200 overflow-hidden">
            <iframe
              title="R.K. Traders Location"
              src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3559.5!2d75.79024!3d26.87255!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMjbCsDUyJzIxLjIiTiA3NcKwNDcnMjQuOSJF!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="google-map-embed"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
